import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, CheckCircle2, Clock, Trophy, Crown } from "lucide-react";

const buildLeaderboard = (referrals, onlyMonth = false) => {
  const monthKey = new Date().toISOString().slice(0, 7);
  const byReferrer = {};
  for (const r of referrals) {
    if (onlyMonth && !(r.created_at || "").startsWith(monthKey)) continue;
    const k = r.referrer_lead_id || r.referrer_name;
    if (!byReferrer[k]) byReferrer[k] = { name: r.referrer_name, total: 0, signed: 0, pending: 0, thisMonth: 0 };
    const e = byReferrer[k];
    e.total += 1;
    if (r.status === "signed") e.signed += 1;
    if (r.reward_status !== "paid") e.pending += 1;
    if ((r.created_at || "").startsWith(monthKey)) e.thisMonth += 1;
  }
  return Object.values(byReferrer)
    .sort((a, b) => b.signed - a.signed || b.total - a.total)
    .slice(0, 5);
};

const RANK_STYLES = ["text-amber-400", "text-slate-300", "text-amber-700"];
import { adminReferrals, adminUpdateReferral, formatApiErrorDetail } from "@/api";

export default function ReferralsTab() {
  const [referrals, setReferrals] = useState([]);

  const load = useCallback(() => {
    adminReferrals().then(setReferrals).catch(() => toast.error("Could not load referrals"));
  }, []);

  useEffect(() => load(), [load]);

  const toggle = async (r) => {
    const next = r.reward_status === "paid" ? "pending" : "paid";
    try {
      await adminUpdateReferral(r.id, next);
      toast.success(next === "paid" ? `Reward marked paid for ${r.referrer_name}` : "Reward moved back to pending");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const leaderboard = buildLeaderboard(referrals);
  const monthLeaders = buildLeaderboard(referrals, true);
  const monthName = new Date().toLocaleDateString("en-ZA", { month: "long", year: "numeric" });

  return (
    <div data-testid="referrals-tab" className="space-y-8">
      {monthLeaders.length > 0 && (
        <div className="rounded-2xl border border-[#F57C00]/40 bg-gradient-to-r from-[#F57C00]/20 via-[#122659] to-[#122659] p-7 flex flex-wrap items-center gap-5" data-testid="referrer-of-month">
          <Crown size={36} className="text-[#F57C00] shrink-0" />
          <div className="flex-1 min-w-48">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#F57C00] font-bold">Referrer of the month — {monthName}</p>
            <p className="font-display text-2xl font-black text-white mt-1" data-testid="referrer-of-month-name">{monthLeaders[0].name}</p>
            <p className="text-xs text-slate-400 mt-1">
              {monthLeaders[0].thisMonth} referral{monthLeaders[0].thisMonth === 1 ? "" : "s"} this month
              {monthLeaders[0].signed > 0 && ` · ${monthLeaders[0].signed} signed`}
            </p>
          </div>
          <p className="text-xs text-slate-500 max-w-52">Your most active advocate this month — consider a bonus thank-you on top of the standard reward.</p>
        </div>
      )}

      <div className="bg-[#122659] border border-sky-300/20 rounded-xl p-8" data-testid="referral-leaderboard">
        <h2 className="font-display text-xl font-black text-white mb-6 flex items-center gap-2">
          <Trophy size={20} className="text-[#F57C00]" /> Top referrers
        </h2>
        {leaderboard.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="leaderboard-empty">No referrals yet — your leaderboard will appear here.</p>
        ) : (
          <div className="space-y-3">
            {leaderboard.map((r, i) => (
              <div key={r.name} data-testid={`leaderboard-row-${i}`} className="flex flex-wrap items-center gap-4 bg-[#0B1F4E] border border-sky-300/10 rounded-lg px-5 py-4">
                <span className={`font-display text-2xl font-black w-8 ${RANK_STYLES[i] || "text-slate-500"}`} data-testid={`leaderboard-rank-${i}`}>#{i + 1}</span>
                <span className="font-bold text-white flex-1 min-w-32" data-testid={`leaderboard-name-${i}`}>{r.name}</span>
                <div className="flex gap-5 text-xs">
                  <span className="text-slate-400">This month <strong className="text-white block text-sm">{r.thisMonth}</strong></span>
                  <span className="text-slate-400">Total leads <strong className="text-white block text-sm">{r.total}</strong></span>
                  <span className="text-slate-400">Signed <strong className="text-emerald-400 block text-sm">{r.signed}</strong></span>
                  <span className="text-slate-400">Rewards due <strong className="text-[#F57C00] block text-sm">{r.pending}</strong></span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-[#122659] border border-sky-300/20 rounded-xl overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-5 border-b border-sky-300/10">
        <div className="flex items-center gap-3">
          <Gift size={18} className="text-[#F57C00]" />
          <h2 className="font-display text-xl font-black text-white">Referral programme</h2>
        </div>
        <p className="text-xs text-slate-500">R500 reward per signed referral — mark paid once settled with the referrer</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="referrals-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-sky-300 border-b border-sky-300/10">
              <th className="px-6 py-4">Referrer</th>
              <th className="px-6 py-4">Referred lead</th>
              <th className="px-6 py-4">Code</th>
              <th className="px-6 py-4">Stage</th>
              <th className="px-6 py-4">Date</th>
              <th className="px-6 py-4 text-right">Reward</th>
            </tr>
          </thead>
          <tbody>
            {referrals.map((r) => (
              <tr key={r.id} data-testid={`referral-row-${r.id}`} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors">
                <td className="px-6 py-4 font-semibold text-white">{r.referrer_name}</td>
                <td className="px-6 py-4 text-slate-300">{r.referred_name}</td>
                <td className="px-6 py-4 font-mono text-xs text-sky-300">{r.code}</td>
                <td className="px-6 py-4">
                  <span data-testid={`referral-stage-${r.id}`} className={`text-[10px] uppercase tracking-wider font-bold rounded-full px-2.5 py-1 border ${r.status === "signed" ? "border-emerald-400/40 text-emerald-400" : "border-amber-500/40 text-amber-400"}`}>
                    {r.status === "signed" ? "Signed" : "Lead"}
                  </span>
                </td>
                <td className="px-6 py-4 text-slate-400 whitespace-nowrap">{(r.created_at || "").slice(0, 10)}</td>
                <td className="px-6 py-4 text-right">
                  <button
                    data-testid={`referral-reward-${r.id}`}
                    onClick={() => toggle(r)}
                    className={`inline-flex items-center gap-1.5 text-xs font-bold px-3.5 py-2 rounded-md border transition-colors ${
                      r.reward_status === "paid"
                        ? "border-emerald-400/40 text-emerald-400 hover:bg-emerald-400 hover:text-[#0B1F4E]"
                        : "border-amber-500/40 text-amber-400 hover:bg-amber-500 hover:text-white"
                    }`}
                  >
                    {r.reward_status === "paid" ? <CheckCircle2 size={13} /> : <Clock size={13} />}
                    {r.reward_status === "paid" ? "Paid" : "Mark paid"}
                  </button>
                </td>
              </tr>
            ))}
            {referrals.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-slate-400" data-testid="referrals-empty">
                  No referrals yet — clients find their shareable referral link in the client portal.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      </div>
    </div>
  );
}
